import { loadSwiper } from './swiper-loader.js';

if (!customElements.get('cards-carousel')) {
    class CardsCarousel extends HTMLElement {
        #desktopMediaQuery = null;
        #lastAnnouncedIndex = null;
        #onViewportChange = () => this.#syncSwiper();

        constructor() {
            super();
            this.swiper = null;
        }

        connectedCallback() {
            this.elCarousel = this.querySelector('[data-carousel]');
            this.elCards = this.querySelectorAll('[data-card]');
            this.elControllers = this.querySelectorAll('[data-card-toggle]');
            this.elButtons = this.querySelectorAll('[data-card-button]');
            this.elAnnounce = this.querySelector('[data-announce]');

            this.elControllers.forEach(el => {
                el.addEventListener('click', () => {
                    const index = Number(el.dataset.cardToggle);

                    if (this.swiper) {
                        if (this.swiper.params.loop && typeof this.swiper.slideToLoop === 'function') {
                            this.swiper.slideToLoop(index);
                        } else {
                            this.swiper.slideTo(index);
                        }
                        return;
                    }

                    this.#selectCard([...this.elCards].find((card) => Number(card.dataset.card) === index));
                });
            });

            this.elButtons.forEach(el => {
                el.addEventListener('click', () => {
                    this.#selectCard(el.closest('[data-card]'));
                });
            });

            this.#desktopMediaQuery = window.matchMedia('(min-width: 768px)');
            this.#desktopMediaQuery.addEventListener('change', this.#onViewportChange);
            this.#syncActiveIndex();
            this.#syncSwiper();
        }

        disconnectedCallback() {
            this.#desktopMediaQuery?.removeEventListener('change', this.#onViewportChange);
            this.#destroySwiper();
        }

        async #syncSwiper() {
            if (this.#desktopMediaQuery.matches) {
                this.#destroySwiper();
                this.setAttribute('data-swiper-ready', '');
                return;
            }

            if (this.swiper) {
                return;
            }

            const Swiper = await loadSwiper();

            if (this.#desktopMediaQuery.matches || this.swiper) {
                return;
            }

            const activeIndex = [...this.elCards].findIndex((card) => card.classList.contains('active'));

            this.swiper = new Swiper(this.elCarousel, {
                enabled: true,
                loop: true,
                slidesPerView: 1,
                speed: 0,
                slideActiveClass: 'active',
                on: {
                    slideChange: (s) => {
                        this.#syncActiveIndex(s.realIndex, { announce: true });
                    }
                }
            });

            if (activeIndex > 0) {
                this.swiper.slideToLoop(activeIndex, 0);
            }

            this.setAttribute('data-swiper-ready', '');
        }

        #destroySwiper() {
            if (!this.swiper) {
                return;
            }

            this.swiper.destroy(true, true);
            this.swiper = null;
        }

        #syncActiveIndex(activeIndex = null, { announce = false } = {}) {
            if (activeIndex === null) {
                const elActive = this.querySelector('.cards-carousel--slide.active');
                activeIndex = elActive ? Number(elActive.dataset.card) : 0;
            }

            this.style.setProperty('--active-index', activeIndex);

            this.elCards.forEach((card) => {
                const isActive = Number(card.dataset.card) === activeIndex;
                card.classList.toggle('active', isActive);
                card.classList.toggle('is-before-active', Number(card.dataset.card) < activeIndex);
            });

            this.elControllers.forEach((el) => {
                const isActive = Number(el.dataset.cardToggle) === activeIndex;
                el.classList.toggle('active', isActive);
                el.setAttribute('aria-pressed', String(isActive));
            });

            if (announce && activeIndex !== this.#lastAnnouncedIndex) {
                this.#announce(activeIndex);
            }

            this.#lastAnnouncedIndex = activeIndex;
        }

        #announce(activeIndex) {
            if (!this.elAnnounce) return;

            const elCard = [...this.elCards].find((card) => Number(card.dataset.card) === activeIndex);
            const title = elCard?.querySelector('.cards-carousel--card-title')?.textContent?.trim();

            if (!title) return;

            const template = this.dataset.labelShowing || 'Showing %title%';
            this.elAnnounce.textContent = template.replace('%title%', title);
        }

        #selectCard(elCard) {
            if (!elCard || !this.#desktopMediaQuery.matches || elCard.classList.contains('active')) {
                return;
            }

            this.#syncActiveIndex(Number(elCard.dataset.card), { announce: true });
        }
    }

    customElements.define('cards-carousel', CardsCarousel);
}
